import { createSlice, type PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/root-state';
import type { PropertyData } from '@/domain/entities/property-object';
import type { PropertyDraftState } from './propertyDraftTypes';
import { batchCreatePropertyObject } from './propertyDraftThunks';

// If you already have cloneData util, import it.
// Keeping local to avoid dependencies.
function cloneData<T>(data: T): T {
  return typeof structuredClone === 'function' ? structuredClone(data) : JSON.parse(JSON.stringify(data));
}

const initialState: PropertyDraftState = {
  draft: null,
  seed: null,
  isDirty: false,
  isSaving: false,
  saveError: null,
  lastCreateResponse: null,
};

export const propertyDraftSlice = createSlice({
  name: 'propertyDraft',
  initialState,
  reducers: {
    // Initialize create mode (hydrated)
    setPropertyDraftSeeded: (state, action: PayloadAction<{ seed: PropertyData }>) => {
      state.seed = cloneData(action.payload.seed);
      state.draft = cloneData(action.payload.seed);
      state.isDirty = false;
    },

    // Reset draft back to seed
    resetPropertyDraft: (state) => {
      if (!state.seed) return;
      state.draft = cloneData(state.seed);
      state.isDirty = false;
    },

    // Replace entire draft (tabs can use this)
    setPropertyDraft: (state, action: PayloadAction<PropertyData>) => {
      state.draft = action.payload;
      state.isDirty = true;
    },

    // Patch only property
    patchPropertyDraftProperty: (state, action: PayloadAction<Partial<NonNullable<PropertyData['property']>>>) => {
      if (!state.draft?.property) return;
      state.draft.property = { ...state.draft.property, ...action.payload };
      state.isDirty = true;
    },

    // Replace whole arrays (tabs can also use patch-by-index if you prefer)
    setPropertyDraftLocations: (state, action: PayloadAction<PropertyData['locations']>) => {
      if (!state.draft) return;
      state.draft.locations = action.payload ?? [];
      state.isDirty = true;
    },

    setPropertyDraftUtilities: (state, action: PayloadAction<PropertyData['utilities']>) => {
      if (!state.draft) return;
      state.draft.utilities = action.payload ?? [];
      state.isDirty = true;
    },

    setPropertyDraftSystems: (state, action: PayloadAction<PropertyData['systems']>) => {
      if (!state.draft) return;
      state.draft.systems = action.payload ?? [];
      state.isDirty = true;
    },

    markPropertyDraftClean: (state) => {
      state.isDirty = false;
    },

    clearPropertyDraft: (state) => {
      state.draft = null;
      state.seed = null;
      state.isDirty = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(batchCreatePropertyObject.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(batchCreatePropertyObject.fulfilled, (state, action) => {
        state.isSaving = false;
        state.saveError = null;

        state.lastCreateResponse = action.payload;

        // ✅ success cleanup (optional)
        state.isDirty = false;
        state.draft = null;
        state.seed = null;
      })
      .addCase(batchCreatePropertyObject.rejected, (state, action) => {
        state.isSaving = false;

        // ✅ message already combined in thunk, but still safe fallback
        const msg =
          (action.payload as { message?: string } | undefined)?.message ??
          action.error.message ??
          'Batch create failed';

        state.saveError = msg;
      });
  },
});

export const {
  setPropertyDraftSeeded,
  resetPropertyDraft,
  setPropertyDraft,
  patchPropertyDraftProperty,
  setPropertyDraftLocations,
  setPropertyDraftUtilities,
  setPropertyDraftSystems,
  markPropertyDraftClean,
  clearPropertyDraft,
} = propertyDraftSlice.actions;

export default propertyDraftSlice.reducer;

/* ---------------- selectors ---------------- */

export const selectPropertyDraftState = (s: RootState) => s.propertyDraft;

export const selectPropertyDraft = createSelector([selectPropertyDraftState], (s) => s.draft);
export const selectPropertyDraftIsDirty = createSelector([selectPropertyDraftState], (s) => s.isDirty);
export const selectPropertyDraftSeed = createSelector([selectPropertyDraftState], (s) => s.seed);

// Convenience selectors so tabs can read like edit-mode selectors
export const selectPropertyDraftProperty = createSelector([selectPropertyDraft], (d) => d?.property);
export const selectPropertyDraftLocations = createSelector([selectPropertyDraft], (d) => d?.locations ?? []);
export const selectPropertyDraftUtilities = createSelector([selectPropertyDraft], (d) => d?.utilities ?? []);
export const selectPropertyDraftSystems = createSelector([selectPropertyDraft], (d) => d?.systems ?? []);
export const selectPropertyDraftSaveError = createSelector([selectPropertyDraftState], (s) => s.saveError);
export const selectPropertyDraftIsSaving = createSelector([selectPropertyDraftState], (s) => s.isSaving);
